import PropTypes from "prop-types";
import { memo, useState, useEffect, useRef } from "react";
import Paragraphs from "app/pages/.shared/Paragraphs";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { ProductsCarousel } from "app/pages/.shared/ProductsCarousel";
import IconRight from "app/pages/.shared/static/icons/IconRight";
import ProductCard from "app/pages/.shared/ProductCard/ProductCard";
import ProductList from "app/pages/Listing/ProductList/ProductList";
import classNames from "classnames";
import "./ProductListBlock.scss";
import Button, {
	BUTTON_ICON_ALIGNMENTS,
	BUTTON_SIZES,
	BUTTON_VARIANTS,
} from "app/pages/.shared/form/Button";
import { FormattedMessage } from "react-intl";
import {
	FilterTrigger,
	FilterDrawer,
	StickyFilterButtonTrigger,
	useFacets,
	FilterSortBar,
} from "app/features/product/productFilters";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { useIntersectionObserver } from "app/hooks";
import { mergeRefs } from "react-merge-refs";

const PRODUCTS_PER_CHUNK = 12;

const ProductListBlock = ({
	title,
	shortVersion,
	shouldHideProductPriceBanner,
	redirectUrl,
	displayFilters = false,
	isMobile,
}) => {
	const { track } = useAnalytics();

	const topOfProductListRef = useRef();

	const [sentinelRef, isIntersecting] = useIntersectionObserver({
		rootMargin: "1px 0px 0px 0px",
	});
	const [loadMoreRef, loadMoreRefInView] = useIntersectionObserver({
		rootMargin: "0px 0px 600px 0px",
	});

	const { filteredProducts, activeFilters } = useFacets();

	const [visibleAccommodationsCount, setVisibleAccommodationsCount] = useState(
		PRODUCTS_PER_CHUNK
	);
	const visibleProducts = filteredProducts?.slice(0, visibleAccommodationsCount);
	const hasMoreToShow = filteredProducts.length > visibleAccommodationsCount;

	const productListBLockTitleClassNames = classNames({
		"product-list-block__title": true,
		"product-list-block__title--with-filter": displayFilters,
	});

	const filterSortBarBackgroundClassName = classNames(
		"product-list-block__filter-sort-bar-background",
		{ "product-list-block__filter-sort-bar-background--stuck": !isIntersecting }
	);

	const handleOpenFilterDrawer = () => {
		track("product-filter-open");
	};

	const handleFilterChange = (facetName, facetValue) => {
		track("pp-filter-facet-clicked", { facetName, facetValue });
	};

	const handleScrollToTop = () => {
		if (!topOfProductListRef.current) {
			return;
		}

		requestAnimationFrame(() => {
			topOfProductListRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
		});
	};

	useEffect(() => {
		if (!loadMoreRefInView) {
			return;
		}

		if (hasMoreToShow) {
			setVisibleAccommodationsCount(prev => prev + PRODUCTS_PER_CHUNK);
		}
	}, [loadMoreRefInView, hasMoreToShow]);

	useEffect(() => {
		setVisibleAccommodationsCount(PRODUCTS_PER_CHUNK);
	}, [activeFilters]);

	let productsNode = null;

	if (isMobile) {
		if (shortVersion) {
			productsNode = (
				<ProductsCarousel
					products={visibleProducts}
					hidePriceBanner={shouldHideProductPriceBanner}
					noSwiping={!isMobile}
				/>
			);
		} else {
			productsNode = (
				<ProductList
					products={visibleProducts}
					hidePriceBanner={shouldHideProductPriceBanner}
					hideMembershipBlock={true}
				/>
			);
		}
	} else if (shortVersion) {
		productsNode = (
			<div className="product-list-block__products">
				{visibleProducts.map((product, index) => {
					return (
						<div key={product.id} data-testid={`product-${product.uri}`}>
							<ProductCard
								product={product}
								position={index}
								hidePriceBanner={shouldHideProductPriceBanner}
							/>
						</div>
					);
				})}
			</div>
		);
	} else {
		productsNode = (
			<ProductList
				products={visibleProducts}
				hidePriceBanner={shouldHideProductPriceBanner}
				hideMembershipBlock={true}
			/>
		);
	}

	return (
		<div className="product-list-block" data-testid="product-list-block">
			<div className="product-list-block__container">
				<div className={productListBLockTitleClassNames}>
					{title && (
						<Typography
							variant={TYPOGRAPHY_VARIANTS.XL3}
							variantMD={TYPOGRAPHY_VARIANTS.XL4}
							isBold
							component="h2"
						>
							<Paragraphs paragraphs={title} enableHTMLFormatting />
						</Typography>
					)}

					{!isMobile && shortVersion && (
						<div className="product-list-block__view-all-offers-link">
							<Button
								variant={BUTTON_VARIANTS.BORDERLESS}
								size={BUTTON_SIZES.LARGE}
								to={redirectUrl}
								label={
									<FormattedMessage id="products.carousel.view.all.offers.label" />
								}
								alignIcon={BUTTON_ICON_ALIGNMENTS.RIGHT}
								icon={<IconRight />}
							/>
						</div>
					)}
				</div>
			</div>

			{displayFilters && !shortVersion && (
				<>
					<div ref={mergeRefs([sentinelRef, topOfProductListRef])} aria-hidden="true" />

					{isMobile ? (
						<>
							<FilterTrigger asChild onClick={handleOpenFilterDrawer}>
								<StickyFilterButtonTrigger />
							</FilterTrigger>

							<FilterDrawer
								isSinglePanelMode
								onChange={handleFilterChange}
								onSubmit={handleScrollToTop}
							/>
						</>
					) : (
						<div className={filterSortBarBackgroundClassName}>
							<FilterSortBar
								isSortAvailable={false}
								className="product-list-block__filter-sort-bar"
								onSubmit={handleScrollToTop}
							/>
						</div>
					)}
				</>
			)}

			<div className="product-list-block__container">
				{productsNode}

				<div ref={loadMoreRef} aria-hidden="true" />

				{isMobile && shortVersion && (
					<div className="product-list-block__view-all-offers-button">
						<Button
							variant={BUTTON_VARIANTS.STROKE}
							size={BUTTON_SIZES.LARGE}
							to={redirectUrl}
							label={
								<FormattedMessage id="products.carousel.view.all.offers.label" />
							}
							alignIcon={BUTTON_ICON_ALIGNMENTS.RIGHT}
							icon={<IconRight />}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

ProductListBlock.propTypes = {
	title: PropTypes.string,
	redirectUrl: PropTypes.string,
	shouldHideProductPriceBanner: PropTypes.bool,
	shortVersion: PropTypes.bool,
	displayFilters: PropTypes.bool,
	isMobile: PropTypes.bool,
};

export default memo(ProductListBlock);
